import { ApiError, api } from "@/lib/apiClient";
import type { GlobalUploadFileEntry, GlobalUploadUploadEntry } from "@/features/globalUpload/globalUploadConstants";

export function buildUploadEntries(
  files: GlobalUploadFileEntry[],
  auftragIds: number[],
): GlobalUploadUploadEntry[] {
  return files.flatMap((entry, fileIndex) =>
    auftragIds.map((auftragId) => ({
      fileIndex,
      auftragId,
      entry,
    })),
  );
}

export type BeauftragungUploadTarget = {
  angebot_id: number;
  file_index: number;
};

export type SchadensmeldungUploadTarget = {
  file_index: number;
};

export async function uploadFilesToAuftraege(
  uploadEntries: GlobalUploadUploadEntry[],
  beauftragungen?: Record<number, BeauftragungUploadTarget[]>,
  schadensmeldungen?: Record<number, SchadensmeldungUploadTarget[]>,
): Promise<void> {
  const formData = new FormData();
  const fileMetadata = uploadEntries.map(({ entry, auftragId, fileIndex }) => ({
    filename: entry.original.name,
    custom_name: entry.customName || entry.original.name,
    auftrag_id: auftragId,
    source_file_index: fileIndex,
  }));

  uploadEntries.forEach(({ entry }) => {
    formData.append("files", entry.original);
  });
  formData.append("file_metadata", JSON.stringify(fileMetadata));
  if (beauftragungen && Object.keys(beauftragungen).length > 0) {
    formData.append("beauftragungen", JSON.stringify(beauftragungen));
  }
  if (schadensmeldungen && Object.keys(schadensmeldungen).length > 0) {
    formData.append("schadensmeldungen", JSON.stringify(schadensmeldungen));
  }

  await api.postForm("/auftraege/upload-files", formData);
}

export interface UploadConflictResult {
  message: string;
  conflictFileIndex: number | null;
}

export function isUploadFilenameConflict(error: ApiError): boolean {
  const body = error.body as { detail?: unknown } | null;
  const detail = body?.detail;
  if (detail && typeof detail === "object" && detail !== null) {
    const obj = detail as { index?: unknown; filename?: unknown };
    return Number.isInteger(Number(obj.index)) && typeof obj.filename === "string";
  }
  if (typeof detail === "string") {
    return /^(dateiname|datei) existiert bereits/i.test(detail);
  }
  return false;
}

export function parseUploadConflict(
  error: ApiError,
  uploadEntries: GlobalUploadUploadEntry[],
  files: GlobalUploadFileEntry[],
): UploadConflictResult {
  const body = error.body as { detail?: string | { index?: number; auftrag_id?: number; filename?: string } } | null;
  const detail = body?.detail;
  let conflictFileIndex: number | null = null;
  let message = "Dateiname existiert bereits. Bitte anpassen.";

  if (detail && typeof detail === "object") {
    const conflictIndex = Number(detail.index);
    if (Number.isInteger(conflictIndex)) {
      const uploadEntry = uploadEntries[conflictIndex];
      if (uploadEntry) conflictFileIndex = uploadEntry.fileIndex;
    }
    if (detail.auftrag_id) {
      message = `Dateiname existiert bereits für Auftrag ${detail.auftrag_id}. Bitte anpassen.`;
    }
  } else if (typeof detail === "string") {
    const conflictName = detail.replace(/^Dateiname existiert bereits:\s*/i, "").trim();
    const idx = files.findIndex(
      (entry) => (entry.customName || entry.original.name || "").trim() === conflictName,
    );
    if (idx >= 0) conflictFileIndex = idx;
  }

  return { message, conflictFileIndex };
}
